import { useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { isValidEmail, toast } from "../../helper/common";
import SkorProtectSelector from "../SkorProtectSelector/SkorProtectSelector";
import { Avatar, Button, Icon, Modal, TextField } from "../design";
import "./CreateGroupScreen.css";

const CreateGroupScreen = ({
  onBack,
  onClose,
  onCreateAndShare,
  user,
  usedFor,
}) => {
  const { _t } = useTranslator();
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [participants, setParticipants] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [creating, setCreating] = useState(false);
  const [skorProtect, setSkorProtect] = useState(true);

  const handleAdd = () => {
    const trimName = nameInput.trim();
    const trimEmail = emailInput.trim().toLowerCase();
    if (!trimName) {
      setEmailError(_t("nameRequired"));
      return;
    }
    if (!trimEmail) return;
    if (!isValidEmail(trimEmail)) {
      setEmailError(_t("invalidEmail"));
      return;
    }
    if (trimEmail === user?.email?.toLowerCase()) {
      setEmailError(_t("cannotAddYourself"));
      return;
    }
    if (participants.some((p) => p.email === trimEmail)) {
      setEmailError(_t("emailAlreadyAdded"));
      return;
    }
    if (participants.length >= 4) {
      setEmailError(_t("maxParticipantsReached"));
      return;
    }
    setParticipants((prev) => [...prev, { name: trimName, email: trimEmail }]);
    setNameInput("");
    setEmailInput("");
    setEmailError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleSubmit = async () => {
    if (participants.length === 0) {
      toast.error(_t("addAtLeastOneEmail"));
      return;
    }
    try {
      setCreating(true);
      await onCreateAndShare(participants, skorProtect);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose ?? onBack}
      dismissible={false}
      align="start"
      title={_t("createANewGroup")}
      onBack={usedFor !== "onboarding" ? onBack : undefined}
      backLabel={_t("back")}
      actions={
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={onBack}
            disabled={creating}
          >
            {_t("back")}
          </Button>
          <Button
            size="lg"
            onClick={handleSubmit}
            loading={creating}
            disabled={participants.length === 0}
          >
            {_t("createGroupAndShare")}
          </Button>
        </>
      }
    >
      <div className="create-group">
        <div className="create-group__card">
          <div className="create-group__intro">
            <h3>{_t("inviteYourFlatmatesByEmail")}</h3>
            <p>{_t("inviteYourFlatmatesByEmailModelDescription")}</p>
          </div>
          <div className="create-group__inputs">
            <TextField
              id="create-group-name"
              placeholder={_t("fullName")}
              value={nameInput}
              error={!!emailError}
              onChange={(e) => {
                setNameInput(e.target.value);
                setEmailError("");
              }}
              onKeyDown={handleKeyDown}
            />
            <TextField
              id="create-group-email"
              type="email"
              placeholder="name@email.com"
              value={emailInput}
              error={!!emailError}
              onChange={(e) => {
                setEmailInput(e.target.value.toLowerCase());
                setEmailError("");
              }}
              onKeyDown={handleKeyDown}
            />
            <Button
              variant="secondary"
              size="lg"
              onClick={handleAdd}
              disabled={!nameInput.trim() || !emailInput.trim()}
            >
              {_t("add")}
            </Button>
          </div>
          {emailError && (
            <p className="create-group__error" role="alert">
              {emailError}
            </p>
          )}

          {participants.length > 0 && (
            <ul className="create-group__list">
              {participants.map(({ name, email }) => (
                <li key={email} className="create-group__person">
                  <Avatar name={name} size="sm" />
                  <div className="create-group__who">
                    <span className="create-group__name">{name}</span>
                    <span className="create-group__email">{email}</span>
                  </div>
                  <span className="create-group__status">
                    {_t("invitationWillBeSent")}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label="remove"
                    iconStart={<Icon name="close" size={16} />}
                    onClick={() =>
                      setParticipants((prev) =>
                        prev.filter((p) => p.email !== email)
                      )
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        <SkorProtectSelector value={skorProtect} onChange={setSkorProtect} />
      </div>
    </Modal>
  );
};

export default CreateGroupScreen;
